<template>
  <li
    ref="navItemRef"
    class="nav-item"
    :class="[{ 'has-children': navigation.children.length }, style]"
    @mouseleave="navigation.children.length && style !== 'mobile' ? collapseSubMenu(navigation.id) : undefined"
    @focusout="onFocusOut"
  >
    <template v-if="navigation.children.length">
      <template v-if="clickableParent">
        <UILinkComponent
          ref="triggerRef"
          :to="navigation?.url"
          class="flex flex-nowrap align-center nav-link"
          :class="[
            { 'nav-with-childs': navigation.children.length },
            style,
            { active: isSubMenuExpanded(navigation.id) },
          ]"
          :aria-label="navigation.name"
          :aria-expanded="isSubMenuExpanded(navigation.id)"
          :aria-controls="getSubMenuId(navigation.id)"
          aria-haspopup="true"
          @mouseenter="!isMobile ? expandSubMenu(navigation.id) : undefined"
        >
          <span>{{ navigation.name }}</span>
        </UILinkComponent>
      </template>
      <template v-else>
        <button
          ref="triggerRef"
          class="flex flex-nowrap align-center nav-link"
          :class="[
            { 'nav-with-childs': navigation.children.length },
            style,
            { active: isSubMenuExpanded(navigation.id) },
          ]"
          :aria-label="navigation.name"
          :aria-expanded="isSubMenuExpanded(navigation.id)"
          :aria-controls="getSubMenuId(navigation.id)"
          aria-haspopup="true"
          @click="isMobile ? toggleSubMenu(navigation.id) : expandSubMenu(navigation.id)"
          @mouseenter="!isMobile ? expandSubMenu(navigation.id) : undefined"
        >
          <span>{{ navigation.name }}</span>
          <UIIconComponent
            v-if="style === 'mobile'"
            class="icon"
            icon="chevron-down"
          />
        </button>
      </template>

      <UIHeaderSubNav
        :id="getSubMenuId(navigation.id)"
        :navigation="getChildren(navigation)"
        :is-open="isSubMenuExpanded(navigation.id)"
        :style="style === 'default' ? undefined : style"
      />
    </template>
    <template v-else>
      <UILinkComponent
        :to="navigation?.url"
        class="flex flex-nowrap align-center nav-link"
        :class="[style]"
        :aria-label="navigation.name"
      >
        <span>
          {{ navigation.name }}
        </span>
      </UILinkComponent>
    </template>
  </li>
</template>

<script lang="ts" setup>
  import type { Navigation, Language } from '@tc8l/nuxt-base'
  import { navNames } from '@/config/navNames'

  const props = withDefaults(
    defineProps<{
      navigation: Navigation
      style?: 'mobile' | 'desktop' | 'default'
      addTopLevelChild?: boolean
      clickableParent?: boolean
    }>(),
    {
      style: 'default',
      addTopLevelChild: true,
      clickableParent: false,
    },
  )

  const localization = useBaseLocalization()
  const currentLanguage = computed(() => localization.getCurrentLanguage())
  const triggerRef = ref<HTMLElement | null>(null)

  const isMobile = computed(() => props.style === 'mobile')

  function getChildren(navigation: Navigation) {
    if (!props.addTopLevelChild) {
      return navigation.children || []
    }

    const { children, ...parentWithoutChildren } = navigation
    const override = navNames[navigation.id as keyof typeof navNames]
    const lang = currentLanguage.value.name
    const topLevelChild = override
      ? { ...parentWithoutChildren, name: override[lang] ?? parentWithoutChildren.name }
      : parentWithoutChildren
    return [topLevelChild as Navigation, ...(children || [])]
  }

  const navItemRef = ref<HTMLElement | null>(null)

  const { getSubMenuId, toggleSubMenu, isSubMenuExpanded, expandSubMenu, collapseSubMenu, resetSubMenus } =
    subMenuHandler()

  function onFocusOut(event: FocusEvent) {
    if (!props.navigation.children.length) return
    const related = event.relatedTarget as Node | null
    if (!navItemRef.value?.contains(related)) {
      collapseSubMenu(props.navigation.id)
    }
  }

  function subMenuHandler() {
    const expandedSubMenus = ref(new Set())
    const getSubMenuId = (id: number) => `sub-menu-${id}`

    const toggleSubMenu = (id: number) => {
      if (expandedSubMenus.value.has(id)) {
        expandedSubMenus.value.delete(id)
      } else {
        expandedSubMenus.value.add(id)
      }
    }

    const isSubMenuExpanded = (id: number) => {
      return expandedSubMenus.value.has(id)
    }

    const expandSubMenu = (id: number) => {
      expandedSubMenus.value.add(id)
    }

    const collapseSubMenu = (id: number) => {
      expandedSubMenus.value.delete(id)
    }

    const handleClickOutside = (event: MouseEvent) => {
      const htmlElement = event?.target as HTMLElement
      if (!htmlElement) return
      const clickedOutside = !htmlElement.closest('.SubNavWrapper, .nav-with-childs')
      if (clickedOutside) {
        expandedSubMenus.value.clear()
      }
    }

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && expandedSubMenus.value.size > 0) {
        expandedSubMenus.value.clear()
        triggerRef.value?.focus()
      }
    }

    onMounted(() => {
      document.addEventListener('click', handleClickOutside)
      document.addEventListener('keydown', handleEscapeKey)
    })

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
      document.removeEventListener('keydown', handleEscapeKey)
    })

    const resetSubMenus = () => expandedSubMenus.value.clear()

    return {
      expandedSubMenus,
      getSubMenuId,
      toggleSubMenu,
      isSubMenuExpanded,
      expandSubMenu,
      collapseSubMenu,
      resetSubMenus,
    }
  }

  function resetMenu() {
    resetSubMenus()
  }

  const router = useRouter()
  router.afterEach(resetMenu)
</script>

<style lang="scss" scoped>
  .nav-item {
    position: relative;
    padding: clamp(2rem, 1.484rem + 1.322vw, 3.6rem) 0;
    font-size: clamp(1.4rem, 1.271rem + 0.331vw, 1.8rem);
    font-weight: 500;

    &.mobile {
      padding: 0;
    }

    .nav-link {
      text-decoration: none;
      transition: 0.3s color var(--ease-fast-in-slow-out);

      .icon {
        transition: 0.3s transform ease;
        transform: rotate(0deg);
        font-size: 2.8rem;
      }

      &.mobile {
        width: 100%;
        font-size: 2.4rem;
        letter-spacing: -0.048rem;
        gap: var(--margin-xs);

        &:after {
          display: none;
        }
      }

      &.active {
        .icon {
          transform: rotate(180deg);
        }

        &:after {
          width: 100%;
        }
      }

      &:after {
        content: '';
        display: inline-block;
        position: absolute;
        left: 50%;
        transition: 0.4s all var(--ease-fast-in-slow-out);
        bottom: 0;
        transform: translateX(-50%);
        z-index: 1;
        height: 4px;
        width: 0;
        background: $beige;
        border-radius: 0.4rem 0.4rem 0 0;
      }

      &:hover {
        color: rgba(0, 0, 0, 0.8);
      }

      &.router-link-active {
        &:after {
          width: 100%;
        }
      }
    }
  }

  @include media(sm) {
    .nav-item {
      font-size: 1.8rem;
      .nav-link {
        &.active {
          margin-bottom: var(--margin-md);
        }
      }
    }
  }
</style>
